import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  Button,
  Chip,
  Divider
} from '@mui/material';
import {
  School,
  Work,
  Code,
  LocationOn
} from '@mui/icons-material';

function About() {
  return (
    <>
      {/* Navigation Bar */}
      <AppBar position="fixed" sx={{ backgroundColor: '#A30000' }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Samuel Hull
          </Typography>
          <Button color="inherit" component={RouterLink} to="/">Home</Button>
          <Button color="inherit" component={RouterLink} to="/about">About</Button>
          <Button color="inherit" component={RouterLink} to="/projects">Projects</Button>
          <Button color="inherit" component={RouterLink} to="/contact">Contact</Button>
        </Toolbar>
      </AppBar>

      {/* About Content */}
      <Container maxWidth="lg" sx={{ mt: 10 }}>
        <Box sx={{ py: 4 }}>
          {/* Header Section */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h2" 
              component="h1" 
              gutterBottom
              sx={{ fontWeight: 'bold', color: '#A30000' }}
            >
              Samuel Hull
            </Typography>
            <Typography variant="h5" sx={{ color: '#666', mb: 2 }}>
              Master's in Computer Science Graduate from Northeastern University & Full Stack Specialist
            </Typography>
            
            {/* Contact Info */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: 3, 
              flexWrap: 'wrap',
              mb: 2 
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOn sx={{ color: '#A30000' }} />
                <Typography>Greater Philadelphia, Pennsylvania (willing to relocate)</Typography>
              </Box>
            </Box>
            <Typography sx={{ color: '#666', fontStyle: 'italic' }}>
              Available: May 4, 2026.
            </Typography>
          </Box>

          {/* Education Section */}
          <Card sx={{ mb: 4, boxShadow: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <School sx={{ fontSize: 40, color: '#A30000', mr: 2 }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  Education
                </Typography>
              </Box>
              
              {/* Northeastern */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Northeastern University, Boston, MA
                    </Typography>
                    <Typography variant="subtitle1" sx={{ color: '#A30000' }}>
                      Master of Science in Computer Science
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontWeight: 'bold' }}>January 2025 – April 2026</Typography>
                    <Typography sx={{ color: '#666' }}>GPA: 3.25/4.0</Typography>
                  </Box>
                </Box>
                <Typography sx={{ mb: 2 }}>
                  <strong>Related courses:</strong> Foundations of Artificial Intelligence, 
                  Advanced Algorithms, Database Management Systems, Web Development, Mobile Application Development, Computer Systems, Programming Design Paradigm
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Temple */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Temple University, Philadelphia, PA
                    </Typography>
                    <Typography variant="subtitle1" sx={{ color: '#A30000' }}>
                      Bachelor of Environmental Studies and Political Science
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontWeight: 'bold' }}>Graduated May 2022</Typography>
                  </Box>
                </Box>
                <Typography sx={{ mb: 2 }}>
                  <strong>Related courses:</strong> Environmental Impact Assessment, Public Policy Analysis, 
                  GIS For Health Data Analytics, Spatial Statistics
                </Typography>
                <Typography sx={{ fontStyle: 'italic', color: '#666' }}>
                  <strong>Research Thesis:</strong> Analyzed the effectiveness of the National Environmental Policy Act 
                  and formulated in-depth policy recommendations on the basis of environmental justice and green energy solutions
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Technical Skills */}
          <Card sx={{ mb: 4, boxShadow: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Code sx={{ fontSize: 40, color: '#A30000', mr: 2 }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  Technical Knowledge
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Programming Languages:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {['Python', 'C', 'Java', 'JavaScript', 'SQL', 'Rust', 'C++', 'Kotlin'].map((skill) => (
                      <Chip 
                        key={skill}
                        label={skill} 
                        variant="outlined" 
                        sx={{ borderColor: '#A30000', color: '#A30000' }}
                      />
                    ))}
                  </Box>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Databases:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {['PostgreSQL', 'MongoDB', 'Oracle', 'MySQL'].map((skill) => (
                      <Chip 
                        key={skill}
                        label={skill} 
                        variant="outlined" 
                        sx={{ borderColor: '#A30000', color: '#A30000' }}
                      />
                    ))}
                  </Box>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Web Technologies:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {['HTML', 'CSS', 'Angular', 'React', 'Node.js', 'Tailwind'].map((skill) => (
                      <Chip 
                        key={skill}
                        label={skill} 
                        variant="outlined" 
                        sx={{ borderColor: '#A30000', color: '#A30000' }}
                      />
                    ))}
                  </Box>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Frameworks, Tools, & Platforms:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {['Spring Boot', 'Docker', 'Kubernetes', 'GitLab', 'CI/CD', 'Amazon Web Services', 'Microsoft Azure', 'Google Cloud Platform', 'Godot Engine', 'FUSE'].map((skill) => (
                      <Chip 
                        key={skill}
                        label={skill} 
                        variant="outlined" 
                        sx={{ borderColor: '#A30000', color: '#A30000' }}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Work Experience */}
          <Card sx={{ mb: 4, boxShadow: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Work sx={{ fontSize: 40, color: '#A30000', mr: 2 }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  Work Experience
                </Typography>
              </Box>
              
              {/* Staff Scientist */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Staff Scientist at Environmental Consulting, Inc.
                    </Typography>
                    <Typography sx={{ color: '#A30000', fontStyle: 'italic' }}>
                      Client Facing Private Environmental Consulting firm
                    </Typography>
                    <Typography sx={{ color: '#666' }}>King of Prussia, PA</Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 'bold' }}>
                    December 2023 – June 2024
                  </Typography>
                </Box>
                <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                  <li>Directed creation of Phase I & II Environmental Assessment Reports, managing structured workflows comparable to technical documentation pipelines.</li>
                  <li>Researched historical site conditions using state, county, and municipal databases, applying methods similar to geospatial analysis.</li>
                  <li>Conducted on-site assessments to identify environmental risks, translating field observations into standardized reports.</li>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Technical Associate */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Technical Associate at K2 Environmental Consulting
                    </Typography>
                    <Typography sx={{ color: '#A30000', fontStyle: 'italic' }}>
                      Consultant firm contracted with the New Jersey Department of Environmental Protection
                    </Typography>
                    <Typography sx={{ color: '#666' }}>Trenton, NJ</Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 'bold' }}>
                    August 2022 – May 2023
                  </Typography>
                </Box>
                <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                  <li>Accelerated implementation of statewide climate programs by streamlining data pipelines, updating reporting systems, and developing emissions-reduction models for EV fleet replacement.</li>
                  <li>Managed approximately $1.5M Natural Climate Solutions grant portfolio, supporting grantees in project design, monitoring, and budgeting.</li>
                  <li>Oversaw ecological restoration projects (afforestation, shoreline restoration, urban tree planting), applying project lifecycle and systems management principles.</li>
                  <li>Built analytical models and reproducible data workflows for the NJ Zero Emission Incentive Program, integrating calculations from funding into scalable frameworks.</li>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </>
  );
}

export default About;